import styled from 'styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { push } from 'redux-first-history';
import { CircularProgress, makeStyles } from '@material-ui/core';
import { getApiPath } from '@/services/Secuirty';
import { PaidNewslettersPaths, pathsParams } from '@/services/dictionaries/Paths';
import { AccessLevel, Newsletter, NewsletterStatusEnum } from '@dev/ee-api/ee-api-models';
import { isNil, map } from 'lodash-es';

import { convertToLocalTime, safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';
import { getPaidNewslettersStore, LoadPreviewTemplate, UpdatePaidNewsletterStatus } from '@/store/paidNewsletters/actions';
import { DropdownOptions } from '@/store/paidNewsletters/types';
import { PaidNewslettersListItem } from '@/store/paidNewsletters/types';
import { useAccessLevel } from '@/services/hooks/useAccessLevel';
import i18n from '@/../i18n';
import {
    CustomTableRowsProps,
    DotsIcon,
    palette,
    SimplePopover,
    SimpleToggle,
    TableRowNameLink,
    TemplatePreview,
    Typography,
} from '@dev/ee-components';
import { parseUrlParams } from '@/services/tools/handleUrlParams';
import { publishedPaidNewsletterNameQueryParam } from '@/components/PaidNewsletters/constants';
import { useDropdownActions } from '../useDropdownActions';
import { useLoadingPaidNewsletters } from '../useLoadingPaidNewsletters';


const TableItemButtonsWrapper = styled.div`
    display: flex;
    justify-content: right;
    align-items: center;
`;
export const ToggleWrapper = styled.div`
    display: flex;
    align-items: center;
`;

export const ToggleLoader = styled.div`
    color: ${palette.indigo[6]};
    margin-right: 19px;
`;

const getRowPopoverOptions = (canModifyPaidNewsletters: boolean): Record<string, string>[] => [
    {
        label: canModifyPaidNewsletters ? i18n.t('editpaidnewsletter') : i18n.t('show'),
        value: DropdownOptions.EditPaidNewsletter,
    },
    {
        label: i18n.t('paidnewsletterlink'),
        value: DropdownOptions.PaidNewsletterLink,
    },
    {
        label: i18n.t('sendcampaign'),
        value: DropdownOptions.SendCampaign,
    },
    ...(canModifyPaidNewsletters ? [{
        label: i18n.t('delete'),
        color: '#ff0000',
        value: DropdownOptions.Delete,
    }] : []),
];

const useStyles = makeStyles(() => ({
    toggle: {
        width: '23px',
    },
   
}));


const getPaidNewsletterThumbnail = (paidNewsletterScreenshot: string ) => paidNewsletterScreenshot ? `${getApiPath()}/userfile${paidNewsletterScreenshot}` : '';

export const useTableRows = (setPreviewContent): CustomTableRowsProps[] => {
    const { t } = useTranslation();
    const paidNewsletters = useSelector(getPaidNewslettersStore);
    const dispatch = useDispatch();
    const classes = useStyles();
    const { getDropdownActions, showPaidNewsletterLinkModal } = useDropdownActions();
    const { hasAccessLevel } = useAccessLevel();
    const canModifyPaidNewsletters = hasAccessLevel(AccessLevel.ModifyPaidNewsletters);
    const queryParams = parseUrlParams(location.search);
    const publishedPaidNewsletterNameRef = useRef(queryParams[publishedPaidNewsletterNameQueryParam] && safelyDecodeBase64Url(queryParams[publishedPaidNewsletterNameQueryParam] as string))
    const [previewClicked, setPreviewClicked] = useState(false);
    useLoadingPaidNewsletters(paidNewsletters);

    useEffect(() => {
        if (isNil(publishedPaidNewsletterNameRef.current) || paidNewsletters?.loadingTemplates) {
            return;
        }
        const publishedPaidNewsletter = paidNewsletters?.templatesList?.find((paidNewsletter) => paidNewsletter.Name === publishedPaidNewsletterNameRef.current);
        if (!publishedPaidNewsletter || publishedPaidNewsletter.Status !== NewsletterStatusEnum.Published ) {
            return;
        }
        showPaidNewsletterLinkModal(publishedPaidNewsletter);
        publishedPaidNewsletterNameRef.current = null;
    }, [paidNewsletters?.loadingTemplates])

    useEffect(() => {
        if (!paidNewsletters.previewLoading && previewClicked) {
            onPreviewClick(paidNewsletters.paidNewsletter);
            setPreviewClicked(false)
        }
    }, [paidNewsletters.previewLoading]); 

    const onPreviewClick = (paidNewsletter: Newsletter): void => {
        const bodyParts = paidNewsletter?.ProductLanding?.Template?.Body?.Parts;
        if (bodyParts) {
            setPreviewContent({ content: bodyParts[0].Content, name: paidNewsletter?.Name });
        }
    };

    const handlePreviewClickCurried = (paidNewsletter) => (paidNewsletterTemplatesThumbnail) => {
            if (!paidNewsletterTemplatesThumbnail) return;      
            dispatch(LoadPreviewTemplate(paidNewsletter.Name));     
            setPreviewClicked(true)
    };

    const handleToggleChange = async(paidNewsletter: PaidNewslettersListItem) => {
        const newStatus = paidNewsletter.Status === NewsletterStatusEnum.Published
        ? NewsletterStatusEnum.Unpublished
        : NewsletterStatusEnum.Published;
        await dispatch(UpdatePaidNewsletterStatus(paidNewsletter.Name, newStatus))
    };
    
    const rowPopoverOptions = getRowPopoverOptions(canModifyPaidNewsletters);

    return map(paidNewsletters?.templatesList, (paidNewsletter: PaidNewslettersListItem): CustomTableRowsProps => {
        
        const paidNewsletterTemplatesThumbnail = getPaidNewsletterThumbnail(paidNewsletter?.ProductScreenshot);

        const handleRowNameLinkClick = (): void => {
            dispatch(
                push(
                    PaidNewslettersPaths.PaidNewsletterCreator.replace(
                        pathsParams.nameOptional,
                        safelyEncodeBase64Url(paidNewsletter.Name)
                    )
                )
            );
        };

        const handleDropdowItemClick = (ev: React.MouseEvent<HTMLElement>, option: { label: string; value: DropdownOptions }): void => {
            getDropdownActions(option, paidNewsletter);
        };

        return {
            preview: (
                <div onClick={(ev): void => ev.stopPropagation()}>
                    <TemplatePreview
                        split={false}
                        screenshot={paidNewsletterTemplatesThumbnail}
                        onClick={() => handlePreviewClickCurried(paidNewsletter)(paidNewsletterTemplatesThumbnail)}
                        disabled={!paidNewsletterTemplatesThumbnail}
                    />
                </div>
            ),
            paidNewsletterName: (
                <div style={{ wordBreak: 'break-all', paddingRight: '10px', paddingTop: '20px' }}>
                    <Typography variant="body1" bodyVariant="onPaper">
                        <TableRowNameLink
                            onClick={handleRowNameLinkClick}
                        >
                            {paidNewsletter.Name}
                        </TableRowNameLink>
                    </Typography>
                    <Typography variant="body1" bodyVariant="onPaper" style={{ marginTop: '24px', marginBottom: '28px' }}>
                        <span style={{ color: palette.gray[5] }}>{t('published')}:</span> {paidNewsletter.FirstPublishedDate ? convertToLocalTime(paidNewsletter.FirstPublishedDate) : null}
                    </Typography>
                </div>
            ),
            status: (
                <ToggleWrapper>
                    {
                        paidNewsletter.isChangingStatus ? 
                        <ToggleLoader><CircularProgress size={20} color="inherit" thickness={5.4} /></ToggleLoader> : 
                        <SimpleToggle
                            id="publish-toggle"
                            value={paidNewsletter.Status}
                            checked={paidNewsletter.Status === NewsletterStatusEnum.Published}
                            onChange={() => handleToggleChange(paidNewsletter)}
                            classes={{ labelRoot: classes.toggle }}
                            disabled={!canModifyPaidNewsletters}
                        />
                    }
                    <Typography variant="body1" bodyVariant="onPaper">{t('published')}</Typography>
                </ToggleWrapper>
            ),
            viewoptions: (
                <TableItemButtonsWrapper>
                    <div onClick={(ev): void => ev.stopPropagation()} style={{ cursor: 'pointer' }}>
                        <SimplePopover
                            type="icon"
                            options={rowPopoverOptions}
                            enableCheckbox={false}
                            placement={"bottom-start"}
                            onItemClick={handleDropdowItemClick}
                        >
                            <DotsIcon />
                        </SimplePopover>
                    </div>
                </TableItemButtonsWrapper>
            ),
            rowID: paidNewsletter.Name,
        };
    });
};

